'use client'

import React, { FC } from 'react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import useCollectionMeta from '@/utils/hooks/useCollectionMeta'

const VerifiedBadge: FC<{
	slug?: string
	className?: string
	interactive?: boolean
	/** When provided, skip the per-row metadata fetch and trust this value (e.g. search results). */
	verified?: boolean
	/** Inscription to open on click; falls back to the fetched gallery inscription id. */
	inscriptionId?: string
}> = ({ slug, className, interactive = true, verified, inscriptionId }) => {
	// Only fetch collection meta when the caller hasn't already told us the verified state.
	const shouldFetch = verified === undefined
	const { data } = useCollectionMeta(shouldFetch ? slug ?? '' : '')
	const isVerified = shouldFetch ? data?.verified : verified
	if (!isVerified) return null

	const resolvedInscriptionId = inscriptionId ?? data?.gallery_inscription_id
	const inscriptionUrl = resolvedInscriptionId
		? `https://ordinals.com/inscription/${resolvedInscriptionId}`
		: undefined

	const badgeIcon = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			width="14"
			height="14"
			aria-hidden="true"
			style={{ color: '#EAB308', minWidth: 14, minHeight: 14 }}
		>
			<path
				fillRule="evenodd"
				d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
				clipRule="evenodd"
			/>
		</svg>
	)

	return (
		<TooltipProvider delayDuration={0}>
			<Tooltip>
				<TooltipTrigger asChild>
					{interactive ? (
						<button
							type="button"
							aria-label="View verification"
							onClick={(e) => {
								e.preventDefault()
								e.stopPropagation()
								if (inscriptionUrl) window.open(inscriptionUrl, '_blank', 'noopener,noreferrer')
							}}
							className={className}
							style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: '2px', flexShrink: 0 }}
						>
							{badgeIcon}
						</button>
					) : (
						<span
							role="img"
							aria-label="Verified collection"
							tabIndex={0}
							className={className}
							style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: '2px', flexShrink: 0 }}
						>
							{badgeIcon}
						</span>
					)}
				</TooltipTrigger>
				<TooltipContent side="top" className="z-[9999]">
					<p className="text-xs">Collections that are onchain galleries get a verified badge</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	)
}

export default VerifiedBadge
